import React, {
  useEffect,
  useRef,
  useState,
  createContext,
  useCallback,
} from "react";
import { Platform, View, Modal, StyleSheet, Text, TouchableOpacity, Image, ActivityIndicator } from "react-native";
import { useFonts, Montserrat_400Regular, Montserrat_600SemiBold, Montserrat_700Bold } from "@expo-google-fonts/montserrat";
import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import { StatusBar as ExpoStatusBar } from "expo-status-bar";
import AsyncStorage from '@react-native-async-storage/async-storage';
import './src/global.css';

// Import all your screens
import OwnershipScreen from "./src/pages/onboarding-screens/ownership_verification_screen";
import NotVehicleOwnerForm from "./src/pages/onboarding-screens/not_vehicle_owner_form";
import VehicleOwnerFormScreen from "./src/pages/onboarding-screens/vehicle_owner_form";
import FindImmatriculationOrChasisNumberScreen from "./src/pages/taxi_verification_screens/FindImmatriculationOrChasisNumber";
import CarRegistrationDocumentUploadScreen from "./src/pages/documents/CarRegistrationDocumentUploadScreen";
import PublicTransportDocumentUploadScreen from "./src/pages/documents/PublicTransportDocumentUploadScreen";
import ReceiptOfTaxPaymentDocumentUploadScreen from "./src/pages/documents/ReceiptOfTaxPaymentDocumentUploadScreen";
import TransportLicenceReceiptScreen from "./src/pages/documents/TransportLicenceReceiptScreen";
import DocumentCompletionScreen from "./src/pages/documents/DocumentCompletionScreen";
import VehicleAdminInfoScreen from "./src/pages/taxi_verification_screens/VehicleAdminInfoScreen";
import VehicleNotFoundInfoScreen from "./src/pages/taxi_verification_screens/VehicleNotFoundInfoScreen";
import HomeScreen from "./src/pages/HomeScreen";
import { LanguageProvider, useTranslation } from "./src/i18n";
import PaymentScreen from "./src/pages/payment/PaymentScreen";
import VehicleNotMatchedInfoScreen from "./src/pages/taxi_verification_screens/VehicleNotMatchedInfoScreen";
import TechnicalInspectionDocumentUploadScreen from "./src/pages/documents/TechnicalInspectionDocumentUploadScreen";
import InsuranceDocumentUploadScreen from "./src/pages/documents/InsuranceDocumentUploadScreen";


export const AppContext = createContext();
const Stack = createStackNavigator();

const COLORS = {
  primary: "#024A59",
  secondary: "#D99178",
  tertiary: "#49b6d6",
  text: "#0b1a1f",
  muted: "#6B7280",
  white: "#ffffff",
  bg: "#F6FAFB"
};

// Button component
const Button = ({ title, onPress, variant = "primary", style }) => {
  const buttonStyle = [
    styles.button,
    variant === "secondary" ? styles.buttonSecondary : styles.buttonPrimary,
    style
  ];
  
  const textStyle = [
    styles.buttonText,
    variant === "secondary" ? styles.buttonTextSecondary : styles.buttonTextPrimary
  ];

  return (
    <TouchableOpacity style={buttonStyle} onPress={onPress}>
      <Text style={textStyle}>{title}</Text>
    </TouchableOpacity>
  );
};

// InstallPrompt component with translation
function InstallPrompt({ visible, onClose, deferredEvent }) {
  const { t } = useTranslation(); // Add translation hook
  const isIOS = Platform.OS === "ios" || (Platform.OS === "web" && /iPad|iPhone|iPod/.test(navigator.userAgent));
  const isAndroid = Platform.OS === "android" || (Platform.OS === "web" && /Android/i.test(navigator.userAgent));
  const canPrompt = !!deferredEvent;

  return (
    <Modal animationType="fade" transparent visible={visible}>
      <View style={styles.modalBackdrop}>
        <View style={styles.modalSheet}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <Image source={require("./assets/icons/yellowtaxi.png")} style={{ width: 35, height: 35, borderRadius: 6 }} />
            <Text style={styles.modalTitle}>{t("install.title")}</Text>
          </View>

          {isIOS ? (
            <Text style={styles.modalText}>
              {t("install.iosInstructions")}
            </Text>
          ) : (
            <View>
              {canPrompt ? (
                <Text style={styles.modalText}>
                  {t("install.promptMessage")}
                </Text>
              ) : (
                <Text style={styles.modalText}>
                  {t("install.manualInstructions")}
                </Text>
              )}
              <Button
                title={canPrompt ? t("install.installNow") : t("common.ok")}
                onPress={() => {
                  if (deferredEvent) deferredEvent.prompt();
                  onClose();
                }}
                variant="secondary"
                style={{ marginTop: 12 }}
              />
            </View>
          )}

          <TouchableOpacity onPress={onClose} style={{ marginTop: 16, alignItems: 'center' }}>
            <Text style={[styles.modalText, {color: COLORS.primary, fontWeight: "600"}]}>{t("install.later")}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

// Loading component
function LoadingScreen() {
  return (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="large" color={COLORS.primary} />
      <Text style={styles.loadingText}>Chargement de votre session...</Text>
    </View>
  );
}

export default function App() {
  const [fontsLoaded] = useFonts({
    Montserrat_400Regular,
    Montserrat_600SemiBold,
    Montserrat_700Bold,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [initialRoute, setInitialRoute] = useState("home_screen");
  
  const [screen, setScreen] = useState("boot");
  const [authed, setAuthed] = useState(false);
  const [profile, setProfile] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const deferredPromptRef = useRef(null);

  // State for form data - initialize with empty values
  const [driverInfo, setDriverInfo] = useState({
    firstName: "",
    lastName: "",
  });

  const [ownerInfo, setOwnerInfo] = useState({
    type: null,
    firstName: "",
    lastName: "",
    organizationName: "",
    procurationFileName: "",
    idCardFileName: "",
    registreCommerceFileName: "",
  });

  // const [verification, setVerification] = useState({
  //   verificationId: null,
  //   folderPrefix: null,
  //   numeimma: null,
  //   owner: null,
  //   driver: null,
  //   pendingUploads: {
  //     ownerId: null,
  //     procurationFile: null,
  //   },
  //   documents: {
  //     carteGrise: [],
  //     carteTransport: [],
  //     recuImpots: [],
  //     licenceTransport: [],
  //   },
  //   vehicleAdmin: {
  //     technicalInspectionValid: null,
  //     technicalInspectionDate: "",
  //     blueCardNumber: "",
  //     blueCardIssueDate: "",
  //     ownerCatLicenseNumber: "",
  //     ownerCatLicenseIssueDate: "",
  //     municipality: "",
  //     municipalityTaxNumber: "",
  //     taxisOperated: "",
  //     driverPhone: "",
  //     driverCapacityNumber: "",
  //     secureBadge: null,
  //     driversRelayCount: "",
  //     macaronNumber: "",
  //   },
  //   actorRole: null, // "OWNER" or "DRIVER"
  //   hasProcuration: null,
  //   ownerAddedDriverPersonIds: [], // Array of personIds for drivers added by owner
  // });

  const [verification, setVerification] = useState({
    verificationId: null,
    folderPrefix: null,
    numeimma: null,
    
    // Owner information (consolidated)
    owner: {
      type: null, // "PHYSIQUE" or "MORALE"
      firstName: "",
      lastName: "",
      fullName: "", // For PHYSIQUE combined name
      organizationName: "", // For MORALE
      gender: "",
      phone: "",
      email: "",
      cinNumber: "",
      taxpayerNumber: "",
      ownerSyndicate: "",
      
      // Owner license details
      categoryLicenseNumber: "",
      categoryLicenseIssueDate: "",
      taxisOperated: "",
      
      // Document flags
      skipIdCard: false,
      skipProcuration: false,
      procurationFileName: null,
      hasProcuration: null,
    },
    
    // Driver information (for driver workflow)
    driver: {
      firstName: "",
      lastName: "",
      fullName: "",
      phone: "",
      email: "",
      birthDate: "",
      gender: "",
      cinNumber: "",
      taxpayerNumber: "",
      licenseNumber: "",
      licenseCategory: "",
      driverSyndicate: "",
      capacityNumber: "",
      hasCapacityDoc: true,
      hasSecureBadgeDoc: true,
      secureBadge: null,
      personId: null,
      isMatched: false,
    },
    
    // Multiple drivers (for owner workflow)
    drivers: [], // Array of driver objects with all fields
    
    // Vehicle basic info
    vehicle: {
      vehicleId: null,
      registrationNumber: "",
      secondaryPlate: "",
      chassisNumber: "",
      brand: "",
      model: "",
      firstRegistrationYear: "",
      seats: "",
      municipality: "",
      blueCardNumber: "",
      blueCardIssueDate: "",
      macaronNumber: "",
      doorNumber: "",
      insuranceStickerValid: null,
    },
    
    // Vehicle admin/survey info
    vehicleAdmin: {
      hasTechInspectionDoc: true,
      hasBlueCardDoc: true,
      hasOwnerCatLicenseDoc: true,
      hasMunicipalityTaxDoc: true,
      hasDriverCapacityDoc: true,
      hasMacaronDoc: true,
      hasSecureBadgeDoc: true,
      
      technicalInspectionValid: null,
      technicalInspectionDate: "",
      blueCardNumber: "",
      blueCardIssueDate: "",
      ownerCatLicenseNumber: "",
      ownerCatLicenseIssueDate: "",
      municipality: "",
      municipalityTaxNumber: "",
      taxisOperated: "",
      driverCapacityNumber: "",
      secureBadge: null,
      driversRelayCount: "",
      macaronNumber: "",
      ownerSyndicate: "",
    },
    
    // Document tracking
    documents: {
      carteGrise: [],
      carteTransport: [],
      recuImpots: [],
      licenceTransport: [],
      technicalInspection: { front: [], back: [], pdf: [] },
      insurance: { front: [], back: [], pdf: [] },
      technicalInspectionSkipped: false,
      insuranceSkipped: false,
      carRegistrationSkipped: false,
      publicTransportSkipped: false,
      taxReceiptSkipped: false,
      transportLicenceSkipped: false,
    },
    
    // Driver documents (file references by personId)
    driverDocuments: {
      // key: personId, value: { photo: file, license: file, idCard: file, capacity: file }
    },
    
    // Pending uploads (temporary)
    pendingUploads: {
      ownerId: null,
      procurationFile: null,
    },
    
    // IDs and references
    actorRole: null, // "OWNER" or "DRIVER"
    ownerAddedDriverPersonIds: [],
    driverPersonId: null,
    driverMatched: false,
    matchedDriverData: null, // Store matched driver info
    
    // Payment info
    isPaymentMade: false,
    paymentInfo: null,
  })
  
  const [formProgress, setFormProgress] = useState({
    currentStep: 'ownership_screen',
    completedSteps: [],
    lastSaved: null,
  });

  // Load saved state on mount - FIXED VERSION
  useEffect(() => {
    const loadSavedState = async () => {
      try {
        console.log("Loading saved state from AsyncStorage... - App.js:343");
        
        const [
          savedVerification,
          savedProgress,
          savedDriverInfo,
          savedOwnerInfo
        ] = await Promise.all([
          AsyncStorage.getItem('verification'),
          AsyncStorage.getItem('formProgress'),
          AsyncStorage.getItem('driverInfo'),
          AsyncStorage.getItem('ownerInfo')
        ]);

        console.log("Loaded verification: - App.js:357", savedVerification);
        console.log("Loaded progress: - App.js:358", savedProgress);
        
        if (savedVerification) {
          const parsedVerification = JSON.parse(savedVerification);
          console.log("Parsed verification: - App.js:362", parsedVerification);
          setVerification(parsedVerification);
        }
        
        if (savedProgress) {
          const parsedProgress = JSON.parse(savedProgress);
          console.log("Parsed progress: - App.js:368", parsedProgress);
          setFormProgress(parsedProgress);
          
          // Set initial route based on saved progress
          if (parsedProgress.currentStep && parsedProgress.currentStep !== 'ownership_screen') {
            console.log("Setting initial route to: - App.js:373", parsedProgress.currentStep);
            setInitialRoute(parsedProgress.currentStep);
          }
        }
        
        if (savedDriverInfo) {
          setDriverInfo(JSON.parse(savedDriverInfo));
        }
        
        if (savedOwnerInfo) {
          setOwnerInfo(JSON.parse(savedOwnerInfo));
        }
        
      } catch (error) {
        console.error('Error loading saved state: - App.js:387', error);
      } finally {
        setIsLoading(false);
        console.log("Loading complete, isLoading set to false - App.js:390");
      }
    };
    
    loadSavedState();
  }, []);

  // Save state when it changes - FIXED VERSION
  useEffect(() => {
    if (!isLoading) { // Only save after initial load
      const saveState = async () => {
        try {
          console.log("Saving state to AsyncStorage... - App.js:402");
          await AsyncStorage.multiSet([
            ['verification', JSON.stringify(verification)],
            ['formProgress', JSON.stringify(formProgress)],
            ['driverInfo', JSON.stringify(driverInfo)],
            ['ownerInfo', JSON.stringify(ownerInfo)]
          ]);
          console.log("State saved successfully - App.js:409");
        } catch (error) {
          console.error('Error saving state: - App.js:411', error);
        }
      };
      
      saveState();
    }
  }, [verification, formProgress, driverInfo, ownerInfo, isLoading]);

  const go = (s) => setScreen(s);

// In App.js, update the clearAllData function

const clearAllData = useCallback(async () => {
  try {
    console.log("Clearing all data... - App.js:425");
    
    // Clear AsyncStorage
    await AsyncStorage.multiRemove([
      'driverInfo',
      'ownerInfo',
      'verification',
      'formProgress'
    ]);
    
    // Reset driverInfo
    setDriverInfo({
      firstName: "",
      lastName: "",
    });
    
    // Reset ownerInfo
    setOwnerInfo({
      type: null,
      firstName: "",
      lastName: "",
      organizationName: "",
      procurationFileName: "",
      idCardFileName: "",
      registreCommerceFileName: "",
    });
    
    // Reset verification state completely
    setVerification({
      verificationId: null,
      folderPrefix: null,
      numeimma: null,
      
      // Owner information
      owner: {
        type: null,
        firstName: "",
        lastName: "",
        fullName: "",
        organizationName: "",
        gender: "",
        phone: "",
        email: "",
        cinNumber: "",
        taxpayerNumber: "",
        ownerSyndicate: "",
        categoryLicenseNumber: "",
        categoryLicenseIssueDate: "",
        taxisOperated: "",
        skipIdCard: false,
        skipProcuration: false,
        procurationFileName: null,
        hasProcuration: null,
      },
      
      // Driver information
      driver: {
        firstName: "",
        lastName: "",
        fullName: "",
        phone: "",
        email: "",
        birthDate: "",
        gender: "",
        cinNumber: "",
        taxpayerNumber: "",
        licenseNumber: "",
        licenseCategory: "",
        driverSyndicate: "",
        capacityNumber: "",
        hasCapacityDoc: true,
        hasSecureBadgeDoc: true,
        secureBadge: null,
        personId: null,
        isMatched: false,
      },
      
      // Multiple drivers
      drivers: [],
      
      // Vehicle basic info
      vehicle: {
        vehicleId: null,
        registrationNumber: "",
        secondaryPlate: "",
        chassisNumber: "",
        brand: "",
        model: "",
        firstRegistrationYear: "",
        seats: "",
        municipality: "",
        blueCardNumber: "",
        blueCardIssueDate: "",
        macaronNumber: "",
        doorNumber: "",
        insuranceStickerValid: null,
      },
      
      // Vehicle admin/survey info
      vehicleAdmin: {
        hasTechInspectionDoc: true,
        hasBlueCardDoc: true,
        hasOwnerCatLicenseDoc: true,
        hasMunicipalityTaxDoc: true,
        hasDriverCapacityDoc: true,
        hasMacaronDoc: true,
        hasSecureBadgeDoc: true,
        technicalInspectionValid: null,
        technicalInspectionDate: "",
        blueCardNumber: "",
        blueCardIssueDate: "",
        ownerCatLicenseNumber: "",
        ownerCatLicenseIssueDate: "",
        municipality: "",
        municipalityTaxNumber: "",
        taxisOperated: "",
        driverCapacityNumber: "",
        secureBadge: null,
        driversRelayCount: "",
        macaronNumber: "",
        ownerSyndicate: "",
      },
      
      // Document tracking
      documents: {
        carteGrise: [],
        carteTransport: [],
        recuImpots: [],
        licenceTransport: [],
        technicalInspection: { front: [], back: [], pdf: [] },
        insurance: { front: [], back: [], pdf: [] },
        technicalInspectionSkipped: false,
        insuranceSkipped: false,
        carRegistrationSkipped: false,
        publicTransportSkipped: false,
        taxReceiptSkipped: false,
        transportLicenceSkipped: false,
      },
      
      // Driver documents
      driverDocuments: {},
      
      // Pending uploads
      pendingUploads: {
        ownerId: null,
        procurationFile: null,
      },
      
      // IDs and references
      actorRole: null,
      ownerAddedDriverPersonIds: [],
      driverPersonId: null,
      driverMatched: false,
      matchedDriverData: null,
      
      // Payment info
      isPaymentMade: false,
      paymentInfo: null,
    });
    
    // Reset form progress
    setFormProgress({
      currentStep: 'ownership_screen',
      completedSteps: [],
      lastSaved: null,
    });
    
    // Reset initial route
    setInitialRoute('ownership_screen');
    
    console.log("All data cleared successfully - App.js:591");
    
  } catch (error) {
    console.error('Error clearing data: - App.js:594', error);
  }
}, [setDriverInfo, setOwnerInfo, setVerification, setFormProgress]);

  function startFlow() {
    if (!authed) return setScreen("auth");
    setScreen("kyc");
  }

  useEffect(() => {
    if (Platform.OS === "web") {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        navigator.standalone === true;

      let timer;
      function showSoon() {
        if (!isStandalone) setShowPrompt(true);
      }

      const onBIP = (e) => {
        e.preventDefault();
        deferredPromptRef.current = e;
        if (!isStandalone) setShowPrompt(true);
      };

      window.addEventListener("beforeinstallprompt", onBIP);
      timer = setTimeout(showSoon, 1200);

      return () => {
        window.removeEventListener("beforeinstallprompt", onBIP);
        if (timer) clearTimeout(timer);
      };
    }
  }, []);

  if (!fontsLoaded || isLoading) {
    return <LoadingScreen />;
  }

  console.log("Rendering with initialRoute: - App.js:634", initialRoute);

  return (
    <LanguageProvider>
      <AppContext.Provider
        value={{
          go,
          authed,
          setAuthed,
          profile,
          setProfile,
          startFlow,
          driverInfo,
          setDriverInfo,
          ownerInfo,
          setOwnerInfo,
          verification,
          setVerification,
          formProgress,
          setFormProgress,
          clearAllData,
        }}
      >
        <View style={styles.container}>
          <NavigationContainer>
            <Stack.Navigator
              initialRouteName={initialRoute}
              screenOptions={{ headerShown: false }}
            >
            <Stack.Screen
                name="home_screen"
                component={HomeScreen}
              />
              <Stack.Screen
                name="ownership_screen"
                component={OwnershipScreen}
              />
              <Stack.Screen
                name="vehicle_owner_form_screen"
                component={VehicleOwnerFormScreen}
              />
              <Stack.Screen
                name="not_vehicle_owner_form_screen"
                component={NotVehicleOwnerForm}
              />
              <Stack.Screen
                name="find_immatriculation_or_chasis_number_screen"
                component={FindImmatriculationOrChasisNumberScreen}
              />
              <Stack.Screen
                name="payment_screen"
                component={PaymentScreen}
              />
              <Stack.Screen
                name="vehicle_admin_info"
                component={VehicleAdminInfoScreen}
              />
              <Stack.Screen
                name="vehicle_not_found_info"
                component={VehicleNotFoundInfoScreen}
              />
              <Stack.Screen
                name="vehicle_not_matched_info"
                component={VehicleNotMatchedInfoScreen}
              />
              <Stack.Screen
                name="car_registration_doc_upload"
                component={CarRegistrationDocumentUploadScreen}
              />
              <Stack.Screen
                name="public_transport_doc_upload"
                component={PublicTransportDocumentUploadScreen}
              />
              <Stack.Screen
                name="tax_payment_receipt_doc_upload"
                component={ReceiptOfTaxPaymentDocumentUploadScreen}
              />
              <Stack.Screen
                name="transport_licence_doc_upload"
                component={TransportLicenceReceiptScreen}
              />
              <Stack.Screen
                name="technical_inspection_doc_upload"
                component={TechnicalInspectionDocumentUploadScreen}
              />

              <Stack.Screen
                name="insurance_doc_upload"
                component={InsuranceDocumentUploadScreen}
              />
              <Stack.Screen
                name="document_upload_completion_screen"
                component={DocumentCompletionScreen}
              />
            </Stack.Navigator>
          </NavigationContainer>
          {Platform.OS === "web" && (
            <InstallPrompt
              visible={showPrompt}
              onClose={() => setShowPrompt(false)}
              deferredEvent={deferredPromptRef.current}
            />
          )}
        </View>
        <ExpoStatusBar style="auto" />
      </AppContext.Provider>
    </LanguageProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'white'
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#024A59',
    fontFamily: "Montserrat_400Regular",
  },
  button: {
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
  },
  buttonPrimary: {
    backgroundColor: COLORS.primary,
  },
  buttonSecondary: {
    backgroundColor: COLORS.secondary,
  },
  buttonText: {
    fontFamily: "Montserrat_700Bold",
    fontSize: 16,
  },
  buttonTextPrimary: {
    color: COLORS.white,
  },
  buttonTextSecondary: {
    color: COLORS.text,
  },
  modalBackdrop: {
    ...Platform.select({
      web: { position: "fixed" },
      default: { position: "absolute" }
    }),
    left: 0, right: 0, top: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalSheet: {
    width: "100%",
    maxWidth: 460,
    backgroundColor: "white",
    borderRadius: 16,
    padding: 16,
  },
  modalTitle: {
    fontFamily: "Montserrat_700Bold",
    fontSize: 18,
    color: COLORS.text,
  },
  modalText: {
    fontFamily: "Montserrat_400Regular",
    color: COLORS.text,
  },
});